import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { NetworkState, ConnectionQuality, ConnectionHistoryEntry, NetworkContextType } from '../types/network';
import { createContextLogger } from '../lib/logger';
import {
  namespacedStorageKey,
  readSessionScopeIdFromStorage,
} from '../lib/tenantStorage';
import { processPendingOperations } from '../lib/apiClient';

const log = createContextLogger('NetworkContext');

const RECONNECT_INTERVAL = 5000;
const MAX_HISTORY_ENTRIES = 20;

const initialState: NetworkState = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  quality: 'good',
  latency: null,
  lastChecked: null,
  lastOnline: null,
  isReconnecting: false,
  failedAttempts: 0,
};

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

interface NetworkProviderProps {
  children: ReactNode;
}

export const NetworkProvider: React.FC<NetworkProviderProps> = ({ children }) => {
  const [state, setState] = useState<NetworkState>(initialState);
  const [history, setHistory] = useState<ConnectionHistoryEntry[]>([]);
  const [pendingOperationsCount, setPendingOperationsCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousOnlineRef = useRef<boolean>(initialState.isOnline);
  const stateRef = useRef(state);
  stateRef.current = state;

  const addHistoryEntry = useCallback((entry: Omit<ConnectionHistoryEntry, 'timestamp'>) => {
    setHistory(prev => {
      const newEntry = { ...entry, timestamp: new Date() };
      const updated = [...prev, newEntry];
      if (updated.length > MAX_HISTORY_ENTRIES) {
        return updated.slice(-MAX_HISTORY_ENTRIES);
      }
      return updated;
    });
  }, []);

  const checkConnection = useCallback(async () => {
    const now = new Date();

    if (!navigator.onLine) {
      if (previousOnlineRef.current) {
        log.warn('Connection lost', { reason: 'browser-offline' });
        addHistoryEntry({ state: 'offline', quality: 'offline', latency: null });
        previousOnlineRef.current = false;
      }

      setState(prev => ({
        ...prev,
        isOnline: false,
        quality: 'offline',
        latency: null,
        lastChecked: now,
        isReconnecting: true,
        failedAttempts: 0,
      }));
      return;
    }

    const wasOffline = !previousOnlineRef.current;
    if (wasOffline) {
      log.info('Connection restored', { source: 'browser-online' });
      addHistoryEntry({ state: 'online', quality: 'good', latency: null });
    }
    previousOnlineRef.current = true;

    setState(prev => ({
      ...prev,
      isOnline: true,
      quality: 'good',
      latency: null,
      lastChecked: now,
      lastOnline: now,
      isReconnecting: false,
      failedAttempts: 0,
    }));
  }, [addHistoryEntry]);

  const startReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) return;

    reconnectTimerRef.current = setInterval(() => {
      if (!stateRef.current.isOnline) {
        log.debug('Attempting to reconnect...');
        checkConnection();
      }
    }, RECONNECT_INTERVAL);
  }, [checkConnection]);

  const stopReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearInterval(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const retryFailedOperations = useCallback(async () => {
    if (!state.isOnline) {
      log.warn('Cannot retry operations while offline');
      return;
    }

    try {
      const result = await processPendingOperations();
      if (result.success > 0 || result.failed > 0) {
        log.info('Offline queue processed', result);
      }
      setPendingOperationsCount(0);
      setLastSyncTime(new Date());
    } catch (error) {
      log.error('Failed to retry operations', { error: String(error) });
    }
  }, [state.isOnline]);

  const updatePendingCount = useCallback(() => {
    try {
      const queueKey = namespacedStorageKey('offline_queue', readSessionScopeIdFromStorage());
      const queuedOps = localStorage.getItem(queueKey) ?? localStorage.getItem('offline_queue');
      if (queuedOps) {
        const operations = JSON.parse(queuedOps);
        setPendingOperationsCount(Array.isArray(operations) ? operations.length : 0);
      } else {
        setPendingOperationsCount(0);
      }
    } catch {
      setPendingOperationsCount(0);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      log.info('Browser reports online');
      checkConnection();
      stopReconnectTimer();
      retryFailedOperations();
    };

    const handleOffline = () => {
      log.warn('Browser reports offline');
      previousOnlineRef.current = false;
      setState(prev => ({
        ...prev,
        isOnline: false,
        quality: 'offline',
        isReconnecting: true,
        failedAttempts: 0,
      }));
      addHistoryEntry({ state: 'offline', quality: 'offline', latency: null });
      startReconnectTimer();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    checkConnection();
    updatePendingCount();

    const storageListener = () => updatePendingCount();
    window.addEventListener('storage', storageListener);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('storage', storageListener);
      stopReconnectTimer();
    };
  }, [checkConnection, startReconnectTimer, stopReconnectTimer, retryFailedOperations, addHistoryEntry, updatePendingCount]);

  useEffect(() => {
    if (!state.isOnline) {
      startReconnectTimer();
    } else {
      stopReconnectTimer();
    }
  }, [state.isOnline, startReconnectTimer, stopReconnectTimer]);

  const value: NetworkContextType = {
    state,
    history,
    checkConnection,
    isOffline: !state.isOnline,
    isSlowConnection: state.quality === 'slow',
    pendingOperationsCount,
    lastSyncTime,
    retryFailedOperations,
  };

  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = (): NetworkContextType => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};
